/* eslint-disable no-empty */
/**
 * Phase A R2 — RemoteEngine
 *
 * Drop-in DjEngine clone used by the pop-out DJ board (DjConsole2 in
 * `mode="popout"`). Proxies every command across a BroadcastChannel to
 * the real engine running in the main room tab. Mirrors the exact
 * DjEngine API shape so the same UI works against either engine.
 *
 * Round 2 additions:
 *   - 4 decks (A, B, C, D)
 *   - Loop rolls / slip toggle / EQ kill / tap-tempo / xfader-assign
 *   - FX rack proxy (enable / param / send-from)
 *   - 8 hot-cue slots
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

class FakeNode {
  constructor(v = 0) { this.value = v; }
  setTargetAtTime(v) { this.value = v; }
}

export class RemoteDeck {
  constructor(label, post) {
    this.label = label;
    this.post = post;
    this.listeners = new Map();

    // State mirror — filled in by `_applyState` when main posts {type:"deck-state"}.
    this.title = "";
    this.duration = 0;
    this.bpm = 0;
    this.pitch = 0;
    this.hotCues = [null, null, null, null, null, null, null, null];
    this.loop = { start: null, end: null, enabled: false };
    this.firstBeatTime = 0;
    this.slipActive = false;
    this.killed = { low: false, mid: false, high: false };
    this._pos = 0;
    this._playing = false;
    this._level = { rms: 0, peak: 0 };

    // Audio-graph stand-ins so DjConsole2's reads don't crash.
    this.audioEl = null;
    this.mediaSrc = null;
    this.preGain  = { gain: new FakeNode(1) };
    this.chanFader = { gain: new FakeNode(1) };
    this.xfadeGain = { gain: new FakeNode(1) };
    this.low  = { gain: new FakeNode(0), frequency: new FakeNode(200), Q: new FakeNode(1) };
    this.mid  = { gain: new FakeNode(0), frequency: new FakeNode(1000), Q: new FakeNode(0.9) };
    this.high = { gain: new FakeNode(0), frequency: new FakeNode(4000), Q: new FakeNode(1) };
    this.filter = { gain: new FakeNode(0), frequency: new FakeNode(22050), Q: new FakeNode(0.7), type: "lowpass" };
    this.analyser = { frequencyBinCount: 128, getByteTimeDomainData: (buf) => buf.fill(128) };
  }

  on(event, cb) {
    const arr = this.listeners.get(event) || [];
    arr.push(cb);
    this.listeners.set(event, arr);
  }
  _emit(event, ...args) { (this.listeners.get(event) || []).forEach((cb) => { try { cb(...args); } catch {} }); }

  // Called by RemoteEngine when main posts {type: "deck-state"}.
  _applyState(s) {
    const prevTitle = this.title;
    const prevPos   = this._pos;
    const prevBpm   = this.bpm;
    const prevPitch = this.pitch;

    this.title    = s.title || "";
    this.duration = s.dur || 0;
    this.bpm      = s.bpm || 0;
    if (typeof s.pitch === "number") this.pitch = s.pitch;
    if (typeof s.firstBeatTime === "number") this.firstBeatTime = s.firstBeatTime;
    if (typeof s.slipActive === "boolean") this.slipActive = s.slipActive;
    if (s.killed) this.killed = { ...this.killed, ...s.killed };
    if (Array.isArray(s.hotCues)) {
      const next = s.hotCues.slice(0, 8);
      while (next.length < 8) next.push(null);
      this.hotCues = next;
    }
    if (s.loop) this.loop = { start: s.loop.start, end: s.loop.end, enabled: !!s.loop.on };
    if (s.level) this._level = s.level;
    // Phase A R6 B2 — flag updated on every deck-state tick. The actual
    // band Float32Array arrives via a separate "deck-bands" message and
    // is cached in `_bands` / `_bandsColumns`.
    if (typeof s.hasBands === "boolean") this._hasBands = s.hasBands;
    this._pos = s.pos || 0;
    this._playing = !!s.playing;

    if (this.title !== prevTitle && this.title) {
      this._emit("loaded", { duration: this.duration, title: this.title });
    }
    if (this._pos !== prevPos) this._emit("positionchange", this._pos);
    if (this.bpm && this.bpm !== prevBpm) this._emit("bpm", this.bpm);
    if (this.pitch !== prevPitch) this._emit("pitchchange", this.pitch);
  }

  // ---- Properties DjConsole2 reads ----
  get currentTime() { return this._pos; }
  get playing()     { return this._playing; }
  get playbackRate(){ return 1 + (this.pitch || 0) / 100; }
  get effectiveBpm(){ return this.bpm ? this.bpm * (1 + (this.pitch || 0) / 100) : 0; }
  isEqKilled(band) { return !!this.killed[band]; }
  beatPhase() {
    if (!this.bpm) return -1;
    const beatSec = 60 / this.effectiveBpm;
    const elapsed = this._pos - (this.firstBeatTime || 0);
    if (elapsed < 0) return 0;
    return ((Math.floor(elapsed / beatSec) % 4) + 4) % 4;
  }

  getLevel() { return this._level; }

  // ---- Commands proxied to the main engine ----
  play()  { this._playing = true;  this.post({ type: "cmd", deck: this.label, action: "play" }); }
  pause() { this._playing = false; this.post({ type: "cmd", deck: this.label, action: "pause" }); }
  cue()   { this.post({ type: "cmd", deck: this.label, action: "cue" }); }
  seekTo(t) { this._pos = t; this.post({ type: "cmd", deck: this.label, action: "seek_to", value: t }); }
  setGain(v)    { this.post({ type: "cmd", deck: this.label, action: "set_gain",    value: v }); }
  setChannel(v) { this.post({ type: "cmd", deck: this.label, action: "set_channel", value: v }); }
  setHigh(v)    { this.post({ type: "cmd", deck: this.label, action: "set_high",    value: v }); }
  setMid(v)     { this.post({ type: "cmd", deck: this.label, action: "set_mid",     value: v }); }
  setLow(v)     { this.post({ type: "cmd", deck: this.label, action: "set_lo",      value: v }); }
  setFilter(v)  { this.post({ type: "cmd", deck: this.label, action: "set_filter",  value: v }); }
  setPitch(v)   {
    this.pitch = clamp(v, -8, 8);
    this.post({ type: "cmd", deck: this.label, action: "set_pitch", value: this.pitch });
  }
  matchBPM(target) {
    if (!this.bpm || !target) return false;
    const ratio = clamp(target / this.bpm, 0.92, 1.08);
    this.setPitch((ratio - 1) * 100);
    return true;
  }
  sync(targetBpm) {
    // Phase A R10 B — sync to a CALLER-PROVIDED LIVE BPM (master deck's
    // effectiveBpm) so the user can sync to a master that has its own
    // pitch already applied. The popout RemoteDeck now sends the live
    // value over BC.
    this.post({ type: "cmd", deck: this.label, action: "sync", targetBpm: targetBpm || 0 });
  }

  setHotCue(slot)   { this.post({ type: "cmd", deck: this.label, action: "hot_cue_set",   slot }); }
  jumpHotCue(slot)  { this.post({ type: "cmd", deck: this.label, action: "hot_cue_jump",  slot }); }
  clearHotCue(slot) { this.post({ type: "cmd", deck: this.label, action: "hot_cue_clear", slot }); }

  setLoop(start, end) { this.loop.start = start; this.loop.end = end; }
  toggleLoop()        { this.post({ type: "cmd", deck: this.label, action: "loop_toggle" }); }

  // Phase A R2 — loop rolls (press-hold) + slip
  startLoop(beats, momentary = false) {
    this.post({ type: "cmd", deck: this.label, action: "loop_roll_start", beats, momentary });
  }
  releaseLoop() {
    this.post({ type: "cmd", deck: this.label, action: "loop_roll_release" });
  }
  setSlip(active) {
    this.slipActive = !!active;
    this.post({ type: "cmd", deck: this.label, action: "slip_toggle", value: !!active });
  }
  killEq(band) {
    this.post({ type: "cmd", deck: this.label, action: "eq_kill", band });
  }
  tapTempo() {
    this.post({ type: "cmd", deck: this.label, action: "tap_tempo" });
    return this.bpm;
  }
  setDownbeatAnchor() {
    this.post({ type: "cmd", deck: this.label, action: "tap_downbeat" });
  }
  // Crossfader assign — proxied to engine.setDeckXfaderAssign
  setXfaderAssign(assign) {
    this.post({ type: "cmd", deck: this.label, action: "set_xfader_assign", assign });
  }

  detectBPM() { return Promise.resolve(this.bpm); }

  // Pop-out picks a local file → ship the File over BC for the main engine.
  async loadFile(file) {
    const url = URL.createObjectURL(file);
    this.title = file.name.replace(/\.[^/.]+$/, "");
    this.audioEl = new Audio();
    this.audioEl.src = url;
    let duration = 0;
    try {
      duration = await new Promise((res) => {
        this.audioEl.onloadedmetadata = () => res(this.audioEl.duration || 0);
        this.audioEl.onerror = () => res(0);
        setTimeout(() => res(this.audioEl?.duration || 0), 4000);
      });
    } catch { /* */ }
    this.duration = duration;
    this.post({ type: "file_picked", deck: this.label, file });
    this._emit("loaded", { duration, title: this.title, url });
    return { duration, title: this.title, url };
  }
}

export class RemoteFxRack {
  constructor(post) {
    this.post = post;
    this._state = {};
  }
  getState() { return this._state; }
  _applyState(state) { this._state = state || {}; }
  setEnabled(name, enabled) { this.post({ type: "fx", action: "enable", name, value: !!enabled }); }
  setParam(name, param, value) { this.post({ type: "fx", action: "param", name, param, value }); }
  setSendFrom(name, source) { this.post({ type: "fx", action: "send", name, source }); }
}

export class RemoteEngine {
  constructor(roomId) {
    this.roomId = roomId;
    this.ctx = null;
    this.crossfader = 0;
    this.crossCurve = "smooth";
    this._vu = { rms: 0, peak: 0 };
    this.master = { gain: new FakeNode(0.8) };
    this.broadcastDest = { stream: { getAudioTracks: () => [] } };

    const post = (msg) => { try { this.bc?.postMessage(msg); } catch {} };
    this._post = post;

    // 4 decks for full parity with DjEngine.
    this.deckA = new RemoteDeck("A", post);
    this.deckB = new RemoteDeck("B", post);
    this.deckC = new RemoteDeck("C", post);
    this.deckD = new RemoteDeck("D", post);
    this.fxRack = new RemoteFxRack(post);

    this.bc = new BroadcastChannel(`stackroom-dj-board-${roomId}`);
    // Phase A R5 followup — autoDj state mirror, populated by main tab
    // broadcasts. Pop-out reads engine.autoDj.playlist; methods proxy
    // commands back via the BC.
    this.autoDj = {
      playlist: [], active: false, currentIndex: 0,
      crossfadeSec: 8, keyMatchEnabled: true, bpmMatchEnabled: true,
      loopPlaylist: false, maxLengthSec: 0,
    };
    this._autoDjListeners = [];
    this.bc.onmessage = (e) => {
      const m = e.data; if (!m || typeof m !== "object") return;
      switch (m.type) {
        case "deck-state": {
          const D = m.deck === "B" ? this.deckB : m.deck === "C" ? this.deckC : m.deck === "D" ? this.deckD : this.deckA;
          D._applyState(m.state);
          return;
        }
        case "deck-bands": {
          // Phase A R6 B2 — cache band Float32Array on RemoteDeck so the
          // pop-out's MirroredWaveform can draw without access to PCM.
          const D = m.deck === "B" ? this.deckB : m.deck === "C" ? this.deckC : m.deck === "D" ? this.deckD : this.deckA;
          D._bands = m.bands;
          D._bandsColumns = m.columns || 0;
          D._hasBands = true;
          D._emit("bands-ready", { columns: D._bandsColumns });
          return;
        }
        case "mixer-state":
          this.crossfader = m.xfader;
          this.master.gain.value = m.master;
          this._vu = m.vu || this._vu;
          return;
        case "fx-state":
          this.fxRack._applyState(m.state);
          return;
        case "autodj-state":
          this._applyAutoDjState(m.state);
          return;
        default: return;
      }
    };

    this._alive = true;
    try { this.bc.postMessage({ type: "hello" }); } catch {}
    this._hbId = setInterval(() => {
      if (!this._alive) return;
      try { this.bc.postMessage({ type: "heartbeat" }); } catch {}
    }, 1000);

    this._onUnload = () => { try { this.bc.postMessage({ type: "goodbye" }); } catch {} };
    window.addEventListener("beforeunload", this._onUnload);
    window.addEventListener("pagehide", this._onUnload);
  }

  _deck(label) {
    return label === "A" ? this.deckA : label === "B" ? this.deckB : label === "C" ? this.deckC : label === "D" ? this.deckD : null;
  }

  setCrossfader(v) {
    this.crossfader = clamp(v, -1, 1);
    this._post({ type: "xfader", value: this.crossfader });
  }
  applyCrossfader() { /* no-op — main handles */ }
  setCrossfaderCurve(_mode) { /* could BC if needed */ }
  setMasterGain(v) {
    this.master.gain.value = clamp(v, 0, 1.2);
    this._post({ type: "master", value: this.master.gain.value });
  }
  // Phase A R5 F1 — proxy Mix Now to main tab.
  mixNow(durationSec) {
    this._post({ type: "mix_now", durationSec });
    return "remote";
  }
  // Phase A R5 followup — Auto-DJ playlist proxy. Both the popout's
  // DjConsole2 and AutoDjPanel read `engine.autoDj.playlist`; mutations
  // get proxied across the BroadcastChannel to the engine in the main tab.
  //
  // File transfer: BroadcastChannel structured-clones File objects in
  // modern browsers, but for reliability and to keep BC payloads small
  // we ship the binary File via window.opener.postMessage (point-to-point
  // back to the main tab) and only the metadata over BC.
  // Phase A R8.1 B2 — File payloads now ship as ArrayBuffer-over-BC, NOT
  // via window.opener.postMessage. Two reasons:
  //   1. window.opener can be null in legitimate scenarios (popout opened
  //      via direct URL, manual navigation, or after refresh) — leaving
  //      Library "+" adds stuck in needs-reattach forever.
  //   2. Single mechanism — same channel as the metadata, no race between
  //      "cmd:autodj:add" arriving via BC and "stackroom-autodj-file"
  //      arriving via window.message.
  // The legacy `_sendFileToOpener` + window.message path stays as a
  // best-effort fallback for unrelated future paths but is no longer used
  // by the canonical "+" / Re-attach flows.
  _sendFileToOpener(trackId, file) {
    try {
      if (!file) {
        // eslint-disable-next-line no-console
        console.warn(`[RemoteEngine] _sendFileToOpener skipped — no file for trackId=${trackId}`);
        return;
      }
      if (typeof window === "undefined") return;
      const opener = window.opener;
      if (!opener || opener.closed) return;
      opener.postMessage(
        { type: "stackroom-autodj-file", roomId: this.roomId, trackId, file },
        "*"
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[RemoteEngine] _sendFileToOpener threw:", e);
    }
  }
  addToPlaylist(track) {
    if (!track || !track.trackId) {
      // eslint-disable-next-line no-console
      console.warn("[RemoteEngine] addToPlaylist rejected — missing track or trackId", track);
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`[+] sending cmd:autodj:add trackId=${track.trackId} name='${track.name}' hasFile=${!!track.file}`);
    const { file, ...meta } = track;
    // Optimistic local mirror first — same in-popout-window state shape
    // the main tab will broadcast back, so React renders the row with
    // hasFile=true immediately and no "needs re-attach" pill flashes.
    try {
      if (this.autoDj.playlist.some((t) => t.trackId === track.trackId)) {
        // eslint-disable-next-line no-console
        console.log(`[+] optimistic skipped — duplicate trackId=${track.trackId}`);
        return;
      }
      this.autoDj.playlist = [...this.autoDj.playlist, { ...meta, file, hasFile: !!file }];
      // eslint-disable-next-line no-console
      console.log(`[+] optimistic mirror playlistLen=${this.autoDj.playlist.length} notifying ${this._autoDjListeners.length} listeners`);
      (this._autoDjListeners || []).forEach((cb) => { try { cb(this.autoDj); } catch { /* */ } });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[+] optimistic mirror threw:", e);
    }
    // Async file delivery via ArrayBuffer-over-BC. The metadata-only post
    // can race ahead so the main tab gets the row in its playlist before
    // the buffer arrives — that's fine, attach is keyed by trackId.
    (async () => {
      if (!file) {
        this._post({ type: "cmd", target: "autodj", action: "add", track: meta });
        return;
      }
      try {
        const ab = await file.arrayBuffer();
        // eslint-disable-next-line no-console
        console.log(`[+] read file as ArrayBuffer: ${ab.byteLength} bytes`);
        this._post({
          type: "cmd", target: "autodj", action: "add",
          track: { ...meta, arrayBuffer: ab, fileName: file.name, mime: file.type },
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[+] failed to read file as ArrayBuffer:", e);
        this._post({ type: "cmd", target: "autodj", action: "add", track: meta });
      }
    })();
  }
  removeFromPlaylist(index)          { this._post({ type: "cmd", target: "autodj", action: "remove",  index }); }
  reorderPlaylist(from, to)          { this._post({ type: "cmd", target: "autodj", action: "reorder", from, to }); }
  attachPlaylistFile(trackId, file)  {
    // Phase A R8.1 — same ArrayBuffer-over-BC mechanism as addToPlaylist.
    // Re-attach is invoked from a Library row whose file the engine no
    // longer has (after a refresh). We re-resolve the File via FSA in
    // the popout's library hook, then ship the bytes over BC. The main
    // engine reconstructs the File and calls attachPlaylistFile, which
    // flips the row's hasFile back to true on the next autodj-state
    // broadcast.
    if (!file) {
      // eslint-disable-next-line no-console
      console.warn(`[+] attachPlaylistFile skipped — no file trackId=${trackId}`);
      return;
    }
    (async () => {
      try {
        const ab = await file.arrayBuffer();
        // eslint-disable-next-line no-console
        console.log(`[+] sending cmd:autodj:attach trackId=${trackId} ab=${ab.byteLength}B`);
        this._post({
          type: "cmd", target: "autodj", action: "attach",
          trackId, arrayBuffer: ab, fileName: file.name, mime: file.type,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[+] attachPlaylistFile read failed:", e);
      }
    })();
  }
  // Phase A R11 A/B — popout-side proxy for engine.transition() (echo /
  // loop styles). Posts a single BC command and awaits the ack from the
  // main tab so the AutoDjPanel runCrossfade flow can `await` it.
  transition(fromLabel, toLabel, style, dur) {
    return new Promise((resolve, reject) => {
      const reqId = `txn-${Math.random().toString(36).slice(2, 10)}`;
      // eslint-disable-next-line no-console
      console.log(`[transition] sending cmd:engine:transition style=${style} from=${fromLabel} to=${toLabel} dur=${dur} reqId=${reqId}`);
      const onAck = (e) => {
        const m = e.data;
        if (!m || m.type !== "transition-done" || m.reqId !== reqId) return;
        clearTimeout(timer);
        this.bc.removeEventListener("message", onAck);
        if (m.ok) {
          // eslint-disable-next-line no-console
          console.log(`[transition] ack OK reqId=${reqId}`);
          resolve();
        } else {
          // eslint-disable-next-line no-console
          console.error(`[transition] ack FAILED reqId=${reqId} error=${m.error}`);
          reject(new Error(m.error || "transition failed"));
        }
      };
      const timer = setTimeout(() => {
        this.bc.removeEventListener("message", onAck);
        // eslint-disable-next-line no-console
        console.error(`[transition] TIMEOUT reqId=${reqId} after 60s`);
        reject(new Error("transition timeout"));
      }, 60000);
      this.bc.addEventListener("message", onAck);
      this._post({ type: "cmd", target: "engine", action: "transition", from: fromLabel, to: toLabel, style, dur, reqId });
    });
  }
  setAutoDj(patch)                   { this._post({ type: "cmd", target: "autodj", action: "set",     patch }); }
  onAutoDj(cb) {
    this._autoDjListeners = this._autoDjListeners || [];
    this._autoDjListeners.push(cb);
    return () => { this._autoDjListeners = this._autoDjListeners.filter((x) => x !== cb); };
  }
  _applyAutoDjState(state) {
    if (!state) return;
    // Phase A R8.2 — when the main tab broadcasts the canonical playlist
    // it strips File objects (BC payload weight). The popout already has
    // the local File reference from the "+" optimistic mirror. Preserve
    // those refs across roundtrips so the popout's Auto-DJ start flow can
    // still find a File on `track.file` (the next step of the load chain
    // was throwing because BC roundtrip blew away the optimistic file).
    const incoming = { ...state };
    if (Array.isArray(incoming.playlist) && Array.isArray(this.autoDj?.playlist)) {
      const prev = this.autoDj.playlist;
      incoming.playlist = incoming.playlist.map((row) => {
        if (row.file) return row;
        const local = prev.find((p) => p.trackId === row.trackId && p.file);
        if (local) {
          return { ...row, file: local.file, hasFile: true };
        }
        return row;
      });
    }
    this.autoDj = incoming;
    (this._autoDjListeners || []).forEach((cb) => { try { cb(this.autoDj); } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[RemoteEngine] _applyAutoDjState listener threw:", err);
    } });
  }
  // Phase A R8.2 — ask the main tab's engine to load a playlist track
  // onto deck A or B. The main tab owns the canonical playlist + Files
  // (R8.1 ArrayBuffer-over-BC reconstructs them there). Instead of
  // re-shipping the File from the popout (which after a BC roundtrip is
  // often null), we look it up by trackId IN THE MAIN TAB and call
  // deck.loadFile there directly. Returns a Promise that resolves when
  // the main tab acks back with the loaded metadata, or rejects on
  // timeout / failure.
  loadPlaylistTrackOntoDeck(trackId, deckLabel) {
    return new Promise((resolve, reject) => {
      if (!trackId) { reject(new Error("loadPlaylistTrackOntoDeck: no trackId")); return; }
      const D = deckLabel === "B" ? "B" : "A";
      const reqId = `load-${Math.random().toString(36).slice(2, 10)}`;
      // eslint-disable-next-line no-console
      console.log(`[autodj] sending cmd:autodj:load-deck trackId=${trackId} deck=${D} reqId=${reqId}`);
      const onAck = (e) => {
        const m = e.data;
        if (!m || m.type !== "autodj-loaded" || m.reqId !== reqId) return;
        clearTimeout(timer);
        this.bc.removeEventListener("message", onAck);
        if (m.ok) {
          // eslint-disable-next-line no-console
          console.log(`[autodj] load-deck ack OK reqId=${reqId} duration=${m.duration} bpm=${m.bpm}`);
          // Update local RemoteDeck mirror so AutoDjPanel sees the loaded state immediately
          const deck = this._deck(D);
          if (deck) {
            deck.title    = m.title || deck.title;
            deck.duration = m.duration || 0;
            deck.bpm      = m.bpm || 0;
            deck._emit("loaded", { duration: deck.duration, title: deck.title });
          }
          resolve({ duration: m.duration, bpm: m.bpm, key: m.key, title: m.title });
        } else {
          // eslint-disable-next-line no-console
          console.error(`[autodj] load-deck ack FAILED reqId=${reqId} error=${m.error}`);
          reject(new Error(m.error || "load-deck failed"));
        }
      };
      const timer = setTimeout(() => {
        this.bc.removeEventListener("message", onAck);
        // eslint-disable-next-line no-console
        console.error(`[autodj] load-deck TIMEOUT reqId=${reqId} trackId=${trackId} after 30s`);
        reject(new Error(`load-deck timeout trackId=${trackId}`));
      }, 30000);
      this.bc.addEventListener("message", onAck);
      this._post({ type: "cmd", target: "autodj", action: "load-deck", trackId, deck: D, reqId });
    });
  }
  setDeckXfaderAssign(label, assign) {
    this._post({ type: "cmd", deck: label, action: "set_xfader_assign", assign });
  }
  readVU() { return this._vu; }
  getBroadcastTrack() { return null; }
  resume() { return Promise.resolve(); }

  dispose() {
    this._alive = false;
    try { this._onUnload(); } catch {}
    clearInterval(this._hbId);
    window.removeEventListener("beforeunload", this._onUnload);
    window.removeEventListener("pagehide", this._onUnload);
    try { this.bc.close(); } catch {}
  }
}
